import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { TeamServiceStrategy, UrlFlagsService, ViewportService } from 'shared';
import { BreadcrumbDropdownItemStore } from '../breadcrumb-dropdown-item.component/breadcrumb-dropdown-item.store';
import { BreadcrumbDropdownItemComponent } from '../breadcrumb-dropdown-item.component/breadcrumb-dropdown-item.component';
import { BreadcrumbHomeItemService } from './breadcrumb-home-item.service';
import { TranslatePipe } from '@ngx-translate/core';
import { ThemedIconComponent } from '../../../../atoms';
import { CreateFormComponent, CreateTeamStrategy } from '../../../../organisms';
import { SidebarListFooterAction } from '../../..';

@Component({
  selector: 'ocp-breadcrumb-home-item',
  imports: [RouterLink, ThemedIconComponent, BreadcrumbDropdownItemComponent, TranslatePipe],
  providers: [BreadcrumbDropdownItemStore],
  styles: `
    ocp-breadcrumb-dropdown-item {
      margin-left: 0.3rem;
    }
    :host {
      display: flex;
      align-items: center;
      flex-shrink: 0;
    }
    .breadcrumb-home-item__link {
      display: flex;
      align-items: center;
      gap: 0.3rem;
      color: inherit;
      text-decoration: none;
      &:hover {
        color: inherit;
      }
    }
    .breadcrumb-home-item__label {
      color: inherit;
    }
  `,
  template: `
    @if (showDropdwn()) {
      <a class="breadcrumb__home" [routerLink]="routerLink()" queryParamsHandling="preserve">
        <ocp-themed-icon [name]="icon()" alt="" />
      </a>
      <ocp-breadcrumb-dropdown-item
        dropdownVariant="avatar"
        [footerActions]="sidebarListFooterAction()"
        (dropdownMenuClick)="onDropdownClick()"
        (footerActionClicked)="onFooterActionClicked()"
      />
    } @else {
      <a
        class="breadcrumb__home breadcrumb-home-item__link"
        [routerLink]="routerLink()"
        queryParamsHandling="preserve"
      >
        <ocp-themed-icon [name]="icon()" alt="" />
        <span class="breadcrumb-home-item__label">{{ 'breadcrumb.home' | translate }}</span>
      </a>
    }
    @if (!hideArrowRight() && !showDropdwn()) {
      <ocp-themed-icon hlmBreadcrumbSeparator class="ml-0.5" name="chevron-right.svg" size="9">
      </ocp-themed-icon>
    }
  `,
})
export class BreadcrumbHomeItemComponent {
  icon = input('home.svg');
  routerLink = input('/');
  hideArrowRight = input(false);

  private readonly router = inject(Router);
  private readonly isEmbedded = inject(UrlFlagsService).embedded;
  private readonly viewportService = inject(ViewportService);
  private readonly teamsService = inject(TeamServiceStrategy);

  protected readonly showDropdwn = computed(
    () => this.viewportService.isMobile() || this.isEmbedded()
  );
  private readonly breadcrumbDropdownItemStore = inject(BreadcrumbDropdownItemStore);
  private readonly breadcrumbHomeItemService = inject(BreadcrumbHomeItemService);

  protected readonly sidebarListFooterAction = signal<SidebarListFooterAction[]>([]);

  constructor() {
    effect(() => {
      if (this.showDropdwn()) {
        this.setDropDownMenuItems();
      }
    });
  }

  protected onDropdownClick() {
    this.setDropDownMenuItems();
  }

  protected onFooterActionClicked(): void {
    this.teamsService.createTeamDialog(CreateFormComponent, new CreateTeamStrategy());
  }

  private setDropDownMenuItems(): void {
    this.breadcrumbHomeItemService.setDropDownMenuItems().subscribe(({ items, hasOwnTeam }) => {
      this.breadcrumbDropdownItemStore.selectedItemRoute.set(this.router.url.split('?')[0]);
      this.breadcrumbDropdownItemStore.dropdownMenuItems.set(items);
      this.sidebarListFooterAction.set(
        hasOwnTeam
          ? []
          : [{ id: '', label: 'breadcrumb.create-new-team', icon: 'plus-circled-bold.svg' }]
      );
    });
  }
}
